import { useCallback, useRef, useEffect } from 'react'
import { useElementsStore } from '@/stores/elementsStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Trash2, Copy, Settings2, Plus, X, ChevronUp, ChevronDown, GripHorizontal } from 'lucide-react'
import { LabelElement, Rotation, ZebraFont, BarcodeElement, ImageElement, GS1DataMatrixElement, GS1Code128Element, GS1ApplicationIdentifierValue } from '@/types'
import { APPLICATION_IDENTIFIERS, getAIDefinition, validateGTINCheckDigit } from '@/lib/gs1/applicationIdentifiers'
import { formatGS1ForHumanReadable } from '@/lib/gs1/gs1Encoder'

// Type guard for barcode elements (excluding GS1-128 which has its own UI)
function isBarcodeElement(element: LabelElement): element is BarcodeElement {
  return element.type.startsWith('barcode-') && element.type !== 'gs1-code128'
}

// Type guard for GS1 elements (both DataMatrix and Code128)
function isGS1Element(element: LabelElement): element is GS1DataMatrixElement | GS1Code128Element {
  return element.type === 'gs1-datamatrix' || element.type === 'gs1-code128'
}

const rotationOptions: { value: Rotation; label: string }[] = [
  { value: 'N', label: '0°' },
  { value: 'R', label: '90°' },
  { value: 'I', label: '180°' },
  { value: 'B', label: '270°' },
]

const fontOptions: { value: ZebraFont; label: string }[] = [
  { value: '0', label: 'Font 0' },
  { value: 'A', label: 'Font A' },
  { value: 'B', label: 'Font B' },
  { value: 'C', label: 'Font C' },
  { value: 'D', label: 'Font D' },
  { value: 'E', label: 'Font E' },
  { value: 'F', label: 'Font F' },
  { value: 'G', label: 'Font G' },
  { value: 'H', label: 'Font H' },
]

const errorCorrectionOptions = [
  { value: 'L', label: 'Low' },
  { value: 'M', label: 'Medium' },
  { value: 'Q', label: 'Quartile' },
  { value: 'H', label: 'High' },
]

export function PropertiesPanel() {
  const {
    selectedId,
    selectedIds,
    elements,
    updateElement,
    updateElements,
    deleteElement,
    deleteElements,
    duplicateElement
  } = useElementsStore()

  const { propertiesPanelHeight, setPropertiesPanelHeight } = useSettingsStore()

  const selectedElements = elements.filter((el) => selectedIds.includes(el.id))
  const element = elements.find((el) => el.id === selectedId)

  // Resize handle logic
  const isResizing = useRef(false)
  const startY = useRef(0)
  const startHeight = useRef(0)

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    startY.current = e.clientY
    startHeight.current = propertiesPanelHeight
    document.body.style.cursor = 'ns-resize'
    document.body.style.userSelect = 'none'
  }, [propertiesPanelHeight])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return
      const deltaY = startY.current - e.clientY
      const newHeight = startHeight.current + deltaY
      setPropertiesPanelHeight(newHeight)
    }

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [setPropertiesPanelHeight])

  // Check if we have multiple elements of the same type selected
  const isMultiSelect = selectedIds.length > 1
  const allSameType = isMultiSelect && selectedElements.every(el => el.type === selectedElements[0]?.type)
  const multiSelectType = allSameType ? selectedElements[0]?.type : null

  // Resize handle component
  const ResizeHandle = () => (
    <div
      className="h-2 border-t bg-card cursor-ns-resize flex items-center justify-center hover:bg-muted/50 active:bg-muted"
      onMouseDown={handleResizeStart}
    >
      <GripHorizontal className="h-3 w-3 text-muted-foreground/50" />
    </div>
  )

  if (!element && !isMultiSelect) {
    return (
      <div className="flex flex-col">
        <ResizeHandle />
        <div style={{ height: propertiesPanelHeight }} className="bg-card flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Settings2 className="h-4 w-4" />
            Select an element to edit properties
          </div>
        </div>
      </div>
    )
  }

  // Multi-select mode with different types
  if (isMultiSelect && !allSameType) {
    return (
      <div className="flex flex-col">
        <ResizeHandle />
        <div style={{ height: propertiesPanelHeight }} className="bg-card flex flex-col">
          <div className="flex items-center gap-4 px-4 py-2 border-b shrink-0">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              <span className="font-medium text-sm">{selectedIds.length} elements selected</span>
            </div>
            <div className="flex gap-1 ml-auto">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => deleteElements(selectedIds)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete All
              </Button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <span className="text-sm text-muted-foreground">
              Select elements of the same type to edit properties in batch
            </span>
          </div>
        </div>
      </div>
    )
  }

  // Multi-select mode with same type elements - batch editing
  if (isMultiSelect && allSameType) {
    const handleBatchUpdate = (updates: Partial<LabelElement>) => {
      updateElements(selectedIds, updates)
    }

    const getMultiSelectTypeName = () => {
      if (multiSelectType === 'text') return 'Text Elements'
      if (multiSelectType === 'qrcode') return 'QR Codes'
      if (multiSelectType === 'datamatrix') return 'DataMatrix'
      if (multiSelectType === 'gs1-datamatrix') return 'GS1 DataMatrix'
      if (multiSelectType === 'gs1-code128') return 'GS1-128'
      if (multiSelectType === 'line') return 'Lines'
      if (multiSelectType === 'rectangle') return 'Rectangles'
      if (multiSelectType === 'circle') return 'Circles'
      if (multiSelectType === 'image') return 'Images'
      if (multiSelectType?.startsWith('barcode-')) return multiSelectType.replace('barcode-', '').toUpperCase() + ' Barcodes'
      return 'Elements'
    }

    // Get mixed value indicator or common value for a property
    const getMixedOrValue = <T,>(prop: string): T | 'mixed' => {
      const values = selectedElements.map(el => (el as unknown as Record<string, unknown>)[prop])
      const firstValue = values[0]
      return values.every(v => v === firstValue) ? firstValue as T : 'mixed'
    }

    return (
      <div className="flex flex-col">
        <ResizeHandle />
        <div style={{ height: propertiesPanelHeight }} className="bg-card flex flex-col">
          <div className="flex items-center gap-4 px-4 py-2 border-b shrink-0">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              <span className="font-medium text-sm">{selectedIds.length} {getMultiSelectTypeName()}</span>
            </div>
            <div className="flex gap-1 ml-auto">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => deleteElements(selectedIds)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete All
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-4 flex flex-wrap gap-x-6 gap-y-3">
              {/* Batch rotation - common to all */}
            <div className="flex items-center gap-2">
              <Label className="text-xs">Rotation</Label>
              <Select
                value={getMixedOrValue<Rotation>('rotation') === 'mixed' ? '' : getMixedOrValue<Rotation>('rotation') as string}
                onValueChange={(value: Rotation) => handleBatchUpdate({ rotation: value })}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue placeholder="Mixed" />
                </SelectTrigger>
                <SelectContent>
                  {rotationOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator orientation="vertical" className="h-8" />

            {/* Text batch properties */}
            {multiSelectType === 'text' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Font</Label>
                  <Select
                    value={getMixedOrValue<ZebraFont>('font') === 'mixed' ? '' : getMixedOrValue<ZebraFont>('font') as string}
                    onValueChange={(value: ZebraFont) => handleBatchUpdate({ font: value })}
                  >
                    <SelectTrigger className="w-24 h-8">
                      <SelectValue placeholder="Mixed" />
                    </SelectTrigger>
                    <SelectContent>
                      {fontOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Size</Label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    placeholder={getMixedOrValue<number>('fontSize') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('fontSize') === 'mixed' ? '' : getMixedOrValue<number>('fontSize')}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ fontSize: value })
                    }}
                    min={10}
                    max={500}
                  />
                </div>
              </>
            )}

            {/* Barcode batch properties */}
            {multiSelectType?.startsWith('barcode-') && multiSelectType !== 'gs1-code128' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Height</Label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    placeholder={getMixedOrValue<number>('height') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('height') === 'mixed' ? '' : getMixedOrValue<number>('height')}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ height: value } as Partial<BarcodeElement>)
                    }}
                    min={5}
                    max={100}
                    step={0.5}
                  />
                  <span className="text-xs text-muted-foreground">mm</span>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Width</Label>
                  <Input
                    type="number"
                    className="w-14 h-8"
                    placeholder={getMixedOrValue<number>('moduleWidth') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('moduleWidth') === 'mixed' ? '' : getMixedOrValue<number>('moduleWidth')}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ moduleWidth: value } as Partial<BarcodeElement>)
                    }}
                    min={1}
                    max={10}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="batch-barcode-showtext"
                    checked={getMixedOrValue<boolean>('showText') === 'mixed' ? false : getMixedOrValue<boolean>('showText') as boolean}
                    onCheckedChange={(checked) => handleBatchUpdate({ showText: checked === true } as Partial<BarcodeElement>)}
                  />
                  <Label htmlFor="batch-barcode-showtext" className="text-xs">
                    Show Text {getMixedOrValue<boolean>('showText') === 'mixed' && '(mixed)'}
                  </Label>
                </div>
              </>
            )}

            {/* QR Code batch properties */}
            {multiSelectType === 'qrcode' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Size</Label>
                  <Input
                    type="number"
                    className="w-14 h-8"
                    placeholder={getMixedOrValue<number>('magnification') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('magnification') === 'mixed' ? '' : getMixedOrValue<number>('magnification')}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ magnification: value })
                    }}
                    min={1}
                    max={10}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">EC</Label>
                  <Select
                    value={getMixedOrValue<string>('errorCorrection') === 'mixed' ? '' : getMixedOrValue<string>('errorCorrection') as string}
                    onValueChange={(value: 'L' | 'M' | 'Q' | 'H') => handleBatchUpdate({ errorCorrection: value })}
                  >
                    <SelectTrigger className="w-24 h-8">
                      <SelectValue placeholder="Mixed" />
                    </SelectTrigger>
                    <SelectContent>
                      {errorCorrectionOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* DataMatrix batch properties */}
            {multiSelectType === 'datamatrix' && (
              <div className="flex items-center gap-2">
                <Label className="text-xs">Size</Label>
                <Input
                  type="number"
                  className="w-14 h-8"
                  placeholder={getMixedOrValue<number>('size') === 'mixed' ? 'Mixed' : undefined}
                  value={getMixedOrValue<number>('size') === 'mixed' ? '' : getMixedOrValue<number>('size')}
                  onChange={(e) => {
                    const value = parseInt(e.target.value)
                    if (!isNaN(value)) handleBatchUpdate({ size: value })
                  }}
                  min={1}
                  max={10}
                />
              </div>
            )}

            {/* Line batch properties */}
            {multiSelectType === 'line' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Width</Label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    placeholder={getMixedOrValue<number>('width') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('width') === 'mixed' ? '' : getMixedOrValue<number>('width')}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ width: value })
                    }}
                    min={0.5}
                    step={0.5}
                  />
                  <span className="text-xs text-muted-foreground">mm</span>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Thickness</Label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    placeholder={getMixedOrValue<number>('height') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('height') === 'mixed' ? '' : getMixedOrValue<number>('height')}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ height: value })
                    }}
                    min={0.1}
                    step={0.1}
                  />
                  <span className="text-xs text-muted-foreground">mm</span>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Color</Label>
                  <Select
                    value={getMixedOrValue<string>('color') === 'mixed' ? '' : getMixedOrValue<string>('color') as string}
                    onValueChange={(value: 'B' | 'W') => handleBatchUpdate({ color: value })}
                  >
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue placeholder="Mixed" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="B">Black</SelectItem>
                      <SelectItem value="W">White</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Rectangle batch properties */}
            {multiSelectType === 'rectangle' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">W</Label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    placeholder={getMixedOrValue<number>('width') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('width') === 'mixed' ? '' : getMixedOrValue<number>('width')}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ width: value })
                    }}
                    min={1}
                    step={0.5}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">H</Label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    placeholder={getMixedOrValue<number>('height') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('height') === 'mixed' ? '' : getMixedOrValue<number>('height')}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ height: value })
                    }}
                    min={1}
                    step={0.5}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Border</Label>
                  <Input
                    type="number"
                    className="w-14 h-8"
                    placeholder={getMixedOrValue<number>('borderThickness') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('borderThickness') === 'mixed' ? '' : getMixedOrValue<number>('borderThickness')}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ borderThickness: value })
                    }}
                    min={1}
                    max={20}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Color</Label>
                  <Select
                    value={getMixedOrValue<string>('color') === 'mixed' ? '' : getMixedOrValue<string>('color') as string}
                    onValueChange={(value: 'B' | 'W') => handleBatchUpdate({ color: value })}
                  >
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue placeholder="Mixed" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="B">Black</SelectItem>
                      <SelectItem value="W">White</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="batch-rect-filled"
                    checked={getMixedOrValue<boolean>('filled') === 'mixed' ? false : getMixedOrValue<boolean>('filled') as boolean}
                    onCheckedChange={(checked) => handleBatchUpdate({ filled: checked === true })}
                  />
                  <Label htmlFor="batch-rect-filled" className="text-xs">
                    Filled {getMixedOrValue<boolean>('filled') === 'mixed' && '(mixed)'}
                  </Label>
                </div>
              </>
            )}

            {/* Circle batch properties */}
            {multiSelectType === 'circle' && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Diameter</Label>
                  <Input
                    type="number"
                    className="w-16 h-8"
                    placeholder={getMixedOrValue<number>('diameter') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('diameter') === 'mixed' ? '' : getMixedOrValue<number>('diameter')}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ diameter: value })
                    }}
                    min={1}
                    step={0.5}
                  />
                  <span className="text-xs text-muted-foreground">mm</span>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Border</Label>
                  <Input
                    type="number"
                    className="w-14 h-8"
                    placeholder={getMixedOrValue<number>('borderThickness') === 'mixed' ? 'Mixed' : undefined}
                    value={getMixedOrValue<number>('borderThickness') === 'mixed' ? '' : getMixedOrValue<number>('borderThickness')}
                    onChange={(e) => {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value)) handleBatchUpdate({ borderThickness: value })
                    }}
                    min={1}
                    max={20}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-xs">Color</Label>
                  <Select
                    value={getMixedOrValue<string>('color') === 'mixed' ? '' : getMixedOrValue<string>('color') as string}
                    onValueChange={(value: 'B' | 'W') => handleBatchUpdate({ color: value })}
                  >
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue placeholder="Mixed" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="B">Black</SelectItem>
                      <SelectItem value="W">White</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            </div>
          </ScrollArea>
        </div>
      </div>
    )
  }

  // At this point, we know element exists (not multi-select and early return passed)
  if (!element) return null

  const handleUpdate = (updates: Partial<LabelElement>) => {
    updateElement(element.id, updates)
  }

  const isBarcode = isBarcodeElement(element)

  const getElementTypeName = () => {
    if (element.type === 'text') return 'Text'
    if (element.type === 'qrcode') return 'QR Code'
    if (element.type === 'datamatrix') return 'DataMatrix'
    if (element.type === 'gs1-datamatrix') return 'GS1 DataMatrix'
    if (element.type === 'gs1-code128') return 'GS1-128'
    if (element.type === 'line') return 'Line'
    if (element.type === 'rectangle') return 'Rectangle'
    if (element.type === 'circle') return 'Circle'
    if (element.type === 'image') return 'Image'
    if (isBarcode) return element.type.replace('barcode-', '').toUpperCase()
    return 'Element'
  }

  // GS1 helper functions (shared by GS1 DataMatrix and GS1-128)
  const handleAddAI = (ai: string) => {
    if (!isGS1Element(element)) return
    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const definition = getAIDefinition(ai)
    const newAI: GS1ApplicationIdentifierValue = {
      ai,
      value: definition?.format === 'date' ? '' : '',
    }
    handleUpdate({
      applicationIdentifiers: [...gs1Element.applicationIdentifiers, newAI]
    } as Partial<GS1DataMatrixElement | GS1Code128Element>)
  }

  const handleUpdateAI = (index: number, updates: Partial<GS1ApplicationIdentifierValue>) => {
    if (!isGS1Element(element)) return
    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const newAIs = [...gs1Element.applicationIdentifiers]
    newAIs[index] = { ...newAIs[index], ...updates }
    handleUpdate({
      applicationIdentifiers: newAIs
    } as Partial<GS1DataMatrixElement | GS1Code128Element>)
  }

  const handleRemoveAI = (index: number) => {
    if (!isGS1Element(element)) return
    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const newAIs = gs1Element.applicationIdentifiers.filter((_, i) => i !== index)
    handleUpdate({
      applicationIdentifiers: newAIs
    } as Partial<GS1DataMatrixElement | GS1Code128Element>)
  }

  // Check if AI can be moved up (respecting ordering rules)
  const canMoveAIUp = (index: number): boolean => {
    if (!isGS1Element(element)) return false
    if (index === 0) return false

    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const ais = gs1Element.applicationIdentifiers
    const currentAI = ais[index].ai
    const previousAI = ais[index - 1].ai

    // GTIN (01) must always be first - cannot move anything above it
    if (previousAI === '01') return false

    // Serial (21) must come after GTIN (01)
    // If trying to move serial up, check if previous position still keeps it after GTIN
    if (currentAI === '21') {
      // Find GTIN position
      const gtinIndex = ais.findIndex(ai => ai.ai === '01')
      // Serial must stay after GTIN
      if (gtinIndex >= 0 && index - 1 <= gtinIndex) return false
    }

    return true
  }

  // Check if AI can be moved down (respecting ordering rules)
  const canMoveAIDown = (index: number): boolean => {
    if (!isGS1Element(element)) return false
    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const ais = gs1Element.applicationIdentifiers

    if (index >= ais.length - 1) return false

    const currentAI = ais[index].ai
    const nextAI = ais[index + 1].ai

    // GTIN (01) must always be first - cannot move it down
    if (currentAI === '01') return false

    // Serial (21) must come after GTIN (01)
    // If trying to move something down past serial, check if serial would still be after GTIN
    if (nextAI === '21') {
      const gtinIndex = ais.findIndex(ai => ai.ai === '01')
      // If current position is right after GTIN, we can't move something past serial
      // as that would push serial closer to or before GTIN
      if (gtinIndex >= 0 && index === gtinIndex) return false
    }

    return true
  }

  // Move AI up in the list
  const handleMoveAIUp = (index: number) => {
    if (!canMoveAIUp(index)) return
    if (!isGS1Element(element)) return

    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const newAIs = [...gs1Element.applicationIdentifiers]
    const temp = newAIs[index]
    newAIs[index] = newAIs[index - 1]
    newAIs[index - 1] = temp

    handleUpdate({
      applicationIdentifiers: newAIs
    } as Partial<GS1DataMatrixElement | GS1Code128Element>)
  }

  // Move AI down in the list
  const handleMoveAIDown = (index: number) => {
    if (!canMoveAIDown(index)) return
    if (!isGS1Element(element)) return

    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const newAIs = [...gs1Element.applicationIdentifiers]
    const temp = newAIs[index]
    newAIs[index] = newAIs[index + 1]
    newAIs[index + 1] = temp

    handleUpdate({
      applicationIdentifiers: newAIs
    } as Partial<GS1DataMatrixElement | GS1Code128Element>)
  }

  // Get available AIs (not already added)
  const getAvailableAIs = () => {
    if (!isGS1Element(element)) return []
    const gs1Element = element as GS1DataMatrixElement | GS1Code128Element
    const usedAIs = new Set(gs1Element.applicationIdentifiers.map(ai => ai.ai))
    return APPLICATION_IDENTIFIERS.filter(def => !usedAIs.has(def.ai))
  }

  return (
    <div className="flex flex-col">
      <ResizeHandle />
      <div style={{ height: propertiesPanelHeight }} className="bg-card flex flex-col">
        <div className="flex items-center gap-4 px-4 py-2 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            <span className="font-medium text-sm">{getElementTypeName()}</span>
          </div>
          <div className="flex gap-1 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => duplicateElement(element.id)}
            >
              <Copy className="h-4 w-4 mr-1" />
              Duplicate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={() => deleteElement(element.id)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
        <div className="p-4 flex flex-wrap gap-x-6 gap-y-3">
        {/* Position - Always visible */}
        <div className="flex items-center gap-2">
          <Label className="text-xs w-8">X</Label>
          <Input
            type="number"
            className="w-20 h-8"
            value={element.position.x.toFixed(1)}
            onChange={(e) =>
              handleUpdate({
                position: { ...element.position, x: parseFloat(e.target.value) || 0 },
              })
            }
            step={0.5}
          />
          <span className="text-xs text-muted-foreground">mm</span>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-xs w-8">Y</Label>
          <Input
            type="number"
            className="w-20 h-8"
            value={element.position.y.toFixed(1)}
            onChange={(e) =>
              handleUpdate({
                position: { ...element.position, y: parseFloat(e.target.value) || 0 },
              })
            }
            step={0.5}
          />
          <span className="text-xs text-muted-foreground">mm</span>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-xs">Rotation</Label>
          <Select
            value={element.rotation}
            onValueChange={(value: Rotation) => handleUpdate({ rotation: value })}
          >
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rotationOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Separator orientation="vertical" className="h-8" />

        {/* Text properties */}
        {element.type === 'text' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Content</Label>
              <Input
                className="w-40 h-8"
                value={element.content}
                onChange={(e) => handleUpdate({ content: e.target.value })}
                placeholder="Text..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Font</Label>
              <Select
                value={element.font}
                onValueChange={(value: ZebraFont) => handleUpdate({ font: value })}
              >
                <SelectTrigger className="w-24 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fontOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Size</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={element.fontSize}
                onChange={(e) => handleUpdate({ fontSize: parseInt(e.target.value) || 30 })}
                min={10}
                max={500}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="text-variable"
                checked={element.isVariable}
                onCheckedChange={(checked) => handleUpdate({ isVariable: checked === true })}
              />
              <Label htmlFor="text-variable" className="text-xs">Variable</Label>
            </div>

            {element.isVariable && (
              <div className="flex items-center gap-2">
                <Label className="text-xs">Var Name</Label>
                <Input
                  className="w-28 h-8"
                  value={element.variableName || ''}
                  onChange={(e) => handleUpdate({ variableName: e.target.value })}
                  placeholder="varName"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="text-reverse"
                checked={element.reverse || false}
                onCheckedChange={(checked) => handleUpdate({ reverse: checked === true })}
              />
              <Label htmlFor="text-reverse" className="text-xs">Reverse</Label>
            </div>
          </>
        )}

        {/* Barcode properties */}
        {isBarcode && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Data</Label>
              <Input
                className="w-32 h-8"
                value={(element as BarcodeElement).content}
                onChange={(e) => handleUpdate({ content: e.target.value } as Partial<BarcodeElement>)}
                placeholder="Barcode data..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Height</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={(element as BarcodeElement).height}
                onChange={(e) => handleUpdate({ height: parseFloat(e.target.value) || 10 } as Partial<BarcodeElement>)}
                min={5}
                max={100}
                step={0.5}
              />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Width</Label>
              <Input
                type="number"
                className="w-14 h-8"
                value={(element as BarcodeElement).moduleWidth}
                onChange={(e) => handleUpdate({ moduleWidth: parseInt(e.target.value) || 2 } as Partial<BarcodeElement>)}
                min={1}
                max={10}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="barcode-showtext"
                checked={(element as BarcodeElement).showText}
                onCheckedChange={(checked) => handleUpdate({ showText: checked === true } as Partial<BarcodeElement>)}
              />
              <Label htmlFor="barcode-showtext" className="text-xs">Show Text</Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="barcode-variable"
                checked={(element as BarcodeElement).isVariable}
                onCheckedChange={(checked) => handleUpdate({ isVariable: checked === true } as Partial<BarcodeElement>)}
              />
              <Label htmlFor="barcode-variable" className="text-xs">Variable</Label>
            </div>

            {(element as BarcodeElement).isVariable && (
              <div className="flex items-center gap-2">
                <Label className="text-xs">Var Name</Label>
                <Input
                  className="w-24 h-8"
                  value={(element as BarcodeElement).variableName || ''}
                  onChange={(e) => handleUpdate({ variableName: e.target.value } as Partial<BarcodeElement>)}
                  placeholder="varName"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="barcode-reverse"
                checked={(element as BarcodeElement).reverse || false}
                onCheckedChange={(checked) => handleUpdate({ reverse: checked === true } as Partial<BarcodeElement>)}
              />
              <Label htmlFor="barcode-reverse" className="text-xs">Reverse</Label>
            </div>
          </>
        )}

        {/* QR Code properties */}
        {element.type === 'qrcode' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Data</Label>
              <Input
                className="w-40 h-8"
                value={element.content}
                onChange={(e) => handleUpdate({ content: e.target.value })}
                placeholder="URL or data..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Size</Label>
              <Input
                type="number"
                className="w-14 h-8"
                value={element.magnification}
                onChange={(e) => handleUpdate({ magnification: parseInt(e.target.value) || 3 })}
                min={1}
                max={10}
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">EC</Label>
              <Select
                value={element.errorCorrection}
                onValueChange={(value: 'L' | 'M' | 'Q' | 'H') => handleUpdate({ errorCorrection: value })}
              >
                <SelectTrigger className="w-24 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {errorCorrectionOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="qr-variable"
                checked={element.isVariable}
                onCheckedChange={(checked) => handleUpdate({ isVariable: checked === true })}
              />
              <Label htmlFor="qr-variable" className="text-xs">Variable</Label>
            </div>

            {element.isVariable && (
              <div className="flex items-center gap-2">
                <Label className="text-xs">Var Name</Label>
                <Input
                  className="w-24 h-8"
                  value={element.variableName || ''}
                  onChange={(e) => handleUpdate({ variableName: e.target.value })}
                  placeholder="varName"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="qr-reverse"
                checked={element.reverse || false}
                onCheckedChange={(checked) => handleUpdate({ reverse: checked === true })}
              />
              <Label htmlFor="qr-reverse" className="text-xs">Reverse</Label>
            </div>
          </>
        )}

        {/* DataMatrix properties */}
        {element.type === 'datamatrix' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Data</Label>
              <Input
                className="w-40 h-8"
                value={element.content}
                onChange={(e) => handleUpdate({ content: e.target.value })}
                placeholder="Data..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Size</Label>
              <Input
                type="number"
                className="w-14 h-8"
                value={element.size}
                onChange={(e) => handleUpdate({ size: parseInt(e.target.value) || 3 })}
                min={1}
                max={10}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="dm-variable"
                checked={element.isVariable}
                onCheckedChange={(checked) => handleUpdate({ isVariable: checked === true })}
              />
              <Label htmlFor="dm-variable" className="text-xs">Variable</Label>
            </div>

            {element.isVariable && (
              <div className="flex items-center gap-2">
                <Label className="text-xs">Var Name</Label>
                <Input
                  className="w-24 h-8"
                  value={element.variableName || ''}
                  onChange={(e) => handleUpdate({ variableName: e.target.value })}
                  placeholder="varName"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="dm-reverse"
                checked={element.reverse || false}
                onCheckedChange={(checked) => handleUpdate({ reverse: checked === true })}
              />
              <Label htmlFor="dm-reverse" className="text-xs">Reverse</Label>
            </div>
          </>
        )}

        {/* GS1 DataMatrix properties */}
        {element.type === 'gs1-datamatrix' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Size</Label>
              <Input
                type="number"
                className="w-14 h-8"
                value={(element as GS1DataMatrixElement).size}
                onChange={(e) => handleUpdate({ size: parseInt(e.target.value) || 3 } as Partial<GS1DataMatrixElement>)}
                min={1}
                max={10}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="gs1-dm-reverse"
                checked={(element as GS1DataMatrixElement).reverse || false}
                onCheckedChange={(checked) => handleUpdate({ reverse: checked === true } as Partial<GS1DataMatrixElement>)}
              />
              <Label htmlFor="gs1-dm-reverse" className="text-xs">Reverse</Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Add AI</Label>
              <Select onValueChange={handleAddAI}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue placeholder="Select AI..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableAIs().map((def) => (
                    <SelectItem key={def.ai} value={def.ai}>
                      ({def.ai}) {def.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Human readable preview */}
            <div className="w-full">
              <Label className="text-xs text-muted-foreground">
                Preview: {formatGS1ForHumanReadable((element as GS1DataMatrixElement).applicationIdentifiers)}
              </Label>
            </div>

            {/* AI List */}
            <div className="w-full space-y-2 pt-2 border-t">
              {(element as GS1DataMatrixElement).applicationIdentifiers.map((aiValue, index) => {
                const definition = getAIDefinition(aiValue.ai)
                const isGTIN = aiValue.ai === '01'
                const isGTINContent = aiValue.ai === '02'
                const isSerial = aiValue.ai === '21'
                // Check GTIN check digit
                const checkDigitWarning = (isGTIN || isGTINContent) && aiValue.value.length === 14 && !aiValue.isVariable
                  ? validateGTINCheckDigit(aiValue.value)
                  : null
                const hasCheckDigitError = checkDigitWarning && !checkDigitWarning.valid && checkDigitWarning.expected !== undefined
                return (
                  <div key={index} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Reorder buttons */}
                      <div className="flex flex-col">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => handleMoveAIUp(index)}
                          disabled={!canMoveAIUp(index)}
                          title={isGTIN ? 'GTIN must be first' : isSerial ? 'Serial must come after GTIN' : 'Move up'}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => handleMoveAIDown(index)}
                          disabled={!canMoveAIDown(index)}
                          title={isGTIN ? 'GTIN must be first' : 'Move down'}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                      <span className="text-xs font-medium w-24">
                        ({aiValue.ai}) {definition?.title || 'Unknown'}
                        {isGTIN && <span className="text-muted-foreground ml-1">(1st)</span>}
                      </span>
                      <Input
                        className={`w-32 h-7 text-xs ${hasCheckDigitError ? 'border-yellow-500' : ''}`}
                        value={aiValue.value}
                        onChange={(e) => handleUpdateAI(index, { value: e.target.value })}
                        placeholder={definition?.format === 'date' ? 'YYMMDD' : `Max ${definition?.maxLength || 20} chars`}
                        maxLength={definition?.maxLength}
                      />
                      <div className="flex items-center gap-1">
                        <Checkbox
                          id={`ai-var-${index}`}
                          checked={aiValue.isVariable || false}
                          onCheckedChange={(checked) => handleUpdateAI(index, { isVariable: checked === true })}
                        />
                        <Label htmlFor={`ai-var-${index}`} className="text-xs">Var</Label>
                      </div>
                      {aiValue.isVariable && (
                        <Input
                          className="w-20 h-7 text-xs"
                          value={aiValue.variableName || ''}
                          onChange={(e) => handleUpdateAI(index, { variableName: e.target.value })}
                          placeholder="varName"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive"
                        onClick={() => handleRemoveAI(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    {hasCheckDigitError && (
                      <span className="text-xs text-yellow-600 dark:text-yellow-400 ml-8">
                        ⚠ Invalid check digit. Expected: {checkDigitWarning.expected}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {/* GS1-128 properties */}
        {element.type === 'gs1-code128' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Height</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={(element as GS1Code128Element).height}
                onChange={(e) => handleUpdate({ height: parseFloat(e.target.value) || 10 } as Partial<GS1Code128Element>)}
                min={5}
                max={100}
                step={0.5}
              />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Width</Label>
              <Input
                type="number"
                className="w-14 h-8"
                value={(element as GS1Code128Element).moduleWidth}
                onChange={(e) => handleUpdate({ moduleWidth: parseInt(e.target.value) || 2 } as Partial<GS1Code128Element>)}
                min={1}
                max={10}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="gs1-128-showtext"
                checked={(element as GS1Code128Element).showText}
                onCheckedChange={(checked) => handleUpdate({ showText: checked === true } as Partial<GS1Code128Element>)}
              />
              <Label htmlFor="gs1-128-showtext" className="text-xs">Show Text</Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="gs1-128-reverse"
                checked={(element as GS1Code128Element).reverse || false}
                onCheckedChange={(checked) => handleUpdate({ reverse: checked === true } as Partial<GS1Code128Element>)}
              />
              <Label htmlFor="gs1-128-reverse" className="text-xs">Reverse</Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Add AI</Label>
              <Select onValueChange={handleAddAI}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue placeholder="Select AI..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableAIs().map((def) => (
                    <SelectItem key={def.ai} value={def.ai}>
                      ({def.ai}) {def.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Human readable preview */}
            <div className="w-full">
              <Label className="text-xs text-muted-foreground">
                Preview: {formatGS1ForHumanReadable((element as GS1Code128Element).applicationIdentifiers)}
              </Label>
            </div>

            {/* AI List */}
            <div className="w-full space-y-2 pt-2 border-t">
              {(element as GS1Code128Element).applicationIdentifiers.map((aiValue, index) => {
                const definition = getAIDefinition(aiValue.ai)
                const isGTIN = aiValue.ai === '01'
                const isGTINContent = aiValue.ai === '02'
                const isSerial = aiValue.ai === '21'
                // Check GTIN check digit
                const checkDigitWarning = (isGTIN || isGTINContent) && aiValue.value.length === 14 && !aiValue.isVariable
                  ? validateGTINCheckDigit(aiValue.value)
                  : null
                const hasCheckDigitError = checkDigitWarning && !checkDigitWarning.valid && checkDigitWarning.expected !== undefined
                return (
                  <div key={index} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Reorder buttons */}
                      <div className="flex flex-col">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => handleMoveAIUp(index)}
                          disabled={!canMoveAIUp(index)}
                          title={isGTIN ? 'GTIN must be first' : isSerial ? 'Serial must come after GTIN' : 'Move up'}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => handleMoveAIDown(index)}
                          disabled={!canMoveAIDown(index)}
                          title={isGTIN ? 'GTIN must be first' : 'Move down'}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                      <span className="text-xs font-medium w-24">
                        ({aiValue.ai}) {definition?.title || 'Unknown'}
                        {isGTIN && <span className="text-muted-foreground ml-1">(1st)</span>}
                      </span>
                      <Input
                        className={`w-32 h-7 text-xs ${hasCheckDigitError ? 'border-yellow-500' : ''}`}
                        value={aiValue.value}
                        onChange={(e) => handleUpdateAI(index, { value: e.target.value })}
                        placeholder={definition?.format === 'date' ? 'YYMMDD' : `Max ${definition?.maxLength || 20} chars`}
                        maxLength={definition?.maxLength}
                      />
                      <div className="flex items-center gap-1">
                        <Checkbox
                          id={`gs1-128-ai-var-${index}`}
                          checked={aiValue.isVariable || false}
                          onCheckedChange={(checked) => handleUpdateAI(index, { isVariable: checked === true })}
                        />
                        <Label htmlFor={`gs1-128-ai-var-${index}`} className="text-xs">Var</Label>
                      </div>
                      {aiValue.isVariable && (
                        <Input
                          className="w-20 h-7 text-xs"
                          value={aiValue.variableName || ''}
                          onChange={(e) => handleUpdateAI(index, { variableName: e.target.value })}
                          placeholder="varName"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive"
                        onClick={() => handleRemoveAI(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    {hasCheckDigitError && (
                      <span className="text-xs text-yellow-600 dark:text-yellow-400 ml-8">
                        ⚠ Invalid check digit. Expected: {checkDigitWarning.expected}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {/* Line properties */}
        {element.type === 'line' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Width</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={element.width}
                onChange={(e) => handleUpdate({ width: parseFloat(e.target.value) || 1 })}
                min={0.5}
                step={0.5}
              />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Thickness</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={element.height}
                onChange={(e) => handleUpdate({ height: parseFloat(e.target.value) || 0.5 })}
                min={0.1}
                step={0.1}
              />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Color</Label>
              <Select
                value={element.color}
                onValueChange={(value: 'B' | 'W') => handleUpdate({ color: value })}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="B">Black</SelectItem>
                  <SelectItem value="W">White</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Rectangle properties */}
        {element.type === 'rectangle' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">W</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={element.width}
                onChange={(e) => handleUpdate({ width: parseFloat(e.target.value) || 10 })}
                min={1}
                step={0.5}
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">H</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={element.height}
                onChange={(e) => handleUpdate({ height: parseFloat(e.target.value) || 10 })}
                min={1}
                step={0.5}
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Border</Label>
              <Input
                type="number"
                className="w-14 h-8"
                value={element.borderThickness}
                onChange={(e) => handleUpdate({ borderThickness: parseInt(e.target.value) || 2 })}
                min={1}
                max={20}
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Color</Label>
              <Select
                value={element.color}
                onValueChange={(value: 'B' | 'W') => handleUpdate({ color: value })}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="B">Black</SelectItem>
                  <SelectItem value="W">White</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="rect-filled"
                checked={element.filled}
                onCheckedChange={(checked) => handleUpdate({ filled: checked === true })}
              />
              <Label htmlFor="rect-filled" className="text-xs">Filled</Label>
            </div>
          </>
        )}

        {/* Circle properties */}
        {element.type === 'circle' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Diameter</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={element.diameter}
                onChange={(e) => handleUpdate({ diameter: parseFloat(e.target.value) || 10 })}
                min={1}
                step={0.5}
              />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Border</Label>
              <Input
                type="number"
                className="w-14 h-8"
                value={element.borderThickness}
                onChange={(e) => handleUpdate({ borderThickness: parseInt(e.target.value) || 2 })}
                min={1}
                max={20}
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Color</Label>
              <Select
                value={element.color}
                onValueChange={(value: 'B' | 'W') => handleUpdate({ color: value })}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="B">Black</SelectItem>
                  <SelectItem value="W">White</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Image properties */}
        {element.type === 'image' && (
          <>
            <div className="flex items-center gap-2">
              <Label className="text-xs">W</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={(element as ImageElement).width.toFixed(1)}
                onChange={(e) => {
                  const newWidth = parseFloat(e.target.value) || 10
                  const aspectRatio = (element as ImageElement).originalWidth / (element as ImageElement).originalHeight
                  handleUpdate({
                    width: newWidth,
                    height: newWidth / aspectRatio
                  } as Partial<ImageElement>)
                }}
                min={1}
                step={0.5}
              />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">H</Label>
              <Input
                type="number"
                className="w-16 h-8"
                value={(element as ImageElement).height.toFixed(1)}
                onChange={(e) => {
                  const newHeight = parseFloat(e.target.value) || 10
                  const aspectRatio = (element as ImageElement).originalWidth / (element as ImageElement).originalHeight
                  handleUpdate({
                    height: newHeight,
                    width: newHeight * aspectRatio
                  } as Partial<ImageElement>)
                }}
                min={1}
                step={0.5}
              />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{(element as ImageElement).name}</span>
              <span>({(element as ImageElement).originalWidth}×{(element as ImageElement).originalHeight}px)</span>
            </div>
          </>
        )}
        </div>
        </ScrollArea>
      </div>
    </div>
  )
}
